import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@ui-kit';
import { SensitiveCell } from './SensitiveCell';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import type { TenantSampleItem, PageResponse } from '../types/tenantSwitcher';

interface SampleGridProps {
  data: PageResponse<TenantSampleItem> | undefined;
  isLoading: boolean;
  isError: boolean;
  errorCode?: string;
  page: number;
  onPageChange: (page: number) => void;
  onAddSample: () => void;
  onRetry: () => void;
}

/** Skeleton 행 */
function SkeletonRow() {
  return (
    <tr>
      {[35, 20, 30, 30, 20].map((w, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${w}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

function formatDateTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'ko' ? 'ko-KR' : 'en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

export function SampleGrid({
  data,
  isLoading,
  isError,
  errorCode,
  page,
  onPageChange,
  onAddSample,
  onRetry,
}: SampleGridProps) {
  const { t, i18n } = useTranslation();

  const isEmpty = !isLoading && !isError && (data?.empty ?? true) && data !== undefined;
  const showTable = !isLoading && !isError && !isEmpty && !!data;

  const HEADERS = useMemo(
    () => [
      t('tenantSwitcher.samples.column.label'),
      t('tenantSwitcher.samples.column.category'),
      t('tenantSwitcher.samples.column.secret'),
      t('tenantSwitcher.samples.column.note'),
      t('tenantSwitcher.samples.column.createdAt'),
    ],
    [t]
  );

  const handlePagePrev = useCallback(() => onPageChange(page - 1), [page, onPageChange]);
  const handlePageNext = useCallback(() => onPageChange(page + 1), [page, onPageChange]);

  const categoryLabel = (cat: string | null) => {
    if (!cat) return '—';
    const key = `tenantSwitcher.category.${cat}`;
    return t(key, { defaultValue: cat });
  };

  return (
    <div
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        background: 'var(--color-surface-background)',
        flex: 1,
        minWidth: 0,
      }}
    >
      {/* 그리드 헤더 바 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          borderBottom: '1px solid var(--color-surface-border)',
          flexWrap: 'wrap',
          gap: 'var(--spacing-2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
          <span
            style={{
              fontWeight: 700,
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
            }}
          >
            {t('tenantSwitcher.samples.title')}
          </span>
          {data && !isLoading && (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {t('tenantSwitcher.samples.totalCount', { count: data.totalElements })}
            </span>
          )}
        </div>

        <ButtonGuard requirePermission="TENANT_SWITCH">
          {({ disabled, reason }) => (
            <button
              type="button"
              onClick={onAddSample}
              disabled={disabled}
              title={reason}
              aria-label={t('tenantSwitcher.samples.button.add')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: disabled ? 'var(--color-surface-elevated)' : 'var(--color-brand-primary)',
                color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                opacity: disabled ? 0.6 : 1,
              }}
            >
              + {t('tenantSwitcher.samples.button.add')}
            </button>
          )}
        </ButtonGuard>
      </div>

      {/* Loading — Skeleton */}
      {isLoading && (
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
          aria-label={t('tenantSwitcher.samples.loadingAriaLabel')}
          aria-busy="true"
        >
          <thead>
            <tr style={{ background: 'var(--color-surface-muted)' }}>
              {HEADERS.map((h) => (
                <th
                  key={h}
                  scope="col"
                  style={{
                    textAlign: 'left',
                    padding: 'var(--spacing-3) var(--spacing-4)',
                    borderBottom: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 600,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </tbody>
        </table>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div
          style={{
            padding: 'var(--spacing-6)',
            textAlign: 'center',
          }}
          role="alert"
        >
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            {errorCode
              ? t(`tenantSwitcher.error.${errorCode}`, {
                  defaultValue: t('tenantSwitcher.error.loadFailed'),
                })
              : t('tenantSwitcher.error.loadFailed')}
          </p>
          <button
            type="button"
            onClick={onRetry}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('tenantSwitcher.error.retry')}
          </button>
        </div>
      )}

      {/* Empty */}
      {isEmpty && (
        <div style={{ padding: 'var(--spacing-6)' }}>
          <EmptyState
            title={t('tenantSwitcher.samples.empty.title')}
            description={t('tenantSwitcher.samples.empty.description')}
            action={
              <ButtonGuard requirePermission="TENANT_SWITCH">
                {({ disabled, reason }) => (
                  <button
                    type="button"
                    onClick={onAddSample}
                    disabled={disabled}
                    title={reason}
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-4)',
                      background: disabled ? 'var(--color-surface-elevated)' : 'var(--color-brand-primary)',
                      color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 600,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      fontFamily: 'var(--font-family-sans)',
                      opacity: disabled ? 0.6 : 1,
                    }}
                  >
                    {t('tenantSwitcher.samples.button.add')}
                  </button>
                )}
              </ButtonGuard>
            }
          />
        </div>
      )}

      {/* Default — 데이터 테이블 */}
      {showTable && data && (
        <>
          <div role="region" aria-label={t('tenantSwitcher.samples.tableAriaLabel')} style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 'var(--font-size-sm)',
              }}
              aria-label={t('tenantSwitcher.samples.tableAriaLabel')}
            >
              <thead>
                <tr style={{ background: 'var(--color-surface-muted)' }}>
                  {HEADERS.map((h) => (
                    <th
                      key={h}
                      scope="col"
                      style={{
                        textAlign: 'left',
                        padding: 'var(--spacing-3) var(--spacing-4)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.content.map((row) => (
                  <tr
                    key={row.id}
                    style={{
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background =
                        'var(--color-surface-muted)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background = '';
                    }}
                  >
                    {/* 라벨 */}
                    <td
                      style={{
                        padding: 'var(--spacing-3) var(--spacing-4)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      {row.label}
                    </td>

                    {/* 분류 */}
                    <td
                      style={{
                        padding: 'var(--spacing-3) var(--spacing-4)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        color: 'var(--color-text-secondary)',
                        fontSize: 'var(--font-size-xs)',
                      }}
                    >
                      {categoryLabel(row.category)}
                    </td>

                    {/* 민감값 (SensitiveCell) */}
                    <td
                      style={{
                        padding: 'var(--spacing-3) var(--spacing-4)',
                        borderBottom: '1px solid var(--color-surface-border)',
                      }}
                    >
                      <SensitiveCell
                        secretMasked={row.secretMasked}
                        secretEncrypted={row.secretEncrypted}
                        rowId={row.id}
                      />
                    </td>

                    {/* 메모 */}
                    <td
                      style={{
                        padding: 'var(--spacing-3) var(--spacing-4)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        color: 'var(--color-text-muted)',
                        fontSize: 'var(--font-size-xs)',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.note ?? '—'}
                    </td>

                    {/* 생성 시각 */}
                    <td
                      style={{
                        padding: 'var(--spacing-3) var(--spacing-4)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-muted)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatDateTime(row.createdAt, i18n.language)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'var(--color-surface-muted)',
              borderTop: '1px solid var(--color-surface-border)',
              flexWrap: 'wrap',
              gap: 'var(--spacing-2)',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {t('tenantSwitcher.pagination.showing', {
                total: data.totalElements.toLocaleString(),
                from: data.number * data.size + 1,
                to: Math.min((data.number + 1) * data.size, data.totalElements),
              })}
            </span>

            <nav
              aria-label={t('tenantSwitcher.pagination.nav')}
              style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}
            >
              <button
                type="button"
                disabled={data.first}
                onClick={handlePagePrev}
                aria-label={t('tenantSwitcher.pagination.prev')}
                style={{
                  minWidth: 28,
                  height: 28,
                  padding: '0 var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: data.first ? 'not-allowed' : 'pointer',
                  opacity: data.first ? 0.5 : 1,
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                &lt;
              </button>

              {Array.from({ length: Math.min(data.totalPages, 10) }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onPageChange(i)}
                  aria-label={t('tenantSwitcher.pagination.page', { page: i + 1 })}
                  aria-current={i === data.number ? 'page' : undefined}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background:
                      i === data.number
                        ? 'var(--color-brand-primary)'
                        : 'var(--color-surface-elevated)',
                    border: `1px solid ${
                      i === data.number
                        ? 'var(--color-brand-primary)'
                        : 'var(--color-surface-border)'
                    }`,
                    color:
                      i === data.number
                        ? 'var(--color-text-inverse)'
                        : 'var(--color-text-secondary)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: 'pointer',
                    fontWeight: i === data.number ? 600 : 400,
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {i + 1}
                </button>
              ))}

              <button
                type="button"
                disabled={data.last}
                onClick={handlePageNext}
                aria-label={t('tenantSwitcher.pagination.next')}
                style={{
                  minWidth: 28,
                  height: 28,
                  padding: '0 var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: data.last ? 'not-allowed' : 'pointer',
                  opacity: data.last ? 0.5 : 1,
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                &gt;
              </button>
            </nav>
          </div>
        </>
      )}
    </div>
  );
}
